// Thin Inertia-based navigation helpers that mirror the react-router API the
// sandbox shell components were written against, so those components port with
// minimal edits. Active state is derived from Inertia's current `usePage().url`.
import { forwardRef, type ComponentProps, type ReactNode } from "react";
import { Link, usePage } from "@inertiajs/react";

function pathOnly(url: string): string {
  return url.split("?")[0]!.split("#")[0]!;
}

/** Current pathname (no query/hash) from the active Inertia page. */
export function useCurrentPath(): string {
  const { url } = usePage();
  return pathOnly(url);
}

/** Active if it's the same path or an ancestor segment of the current path. */
export function isPathActive(pathname: string, to: string): boolean {
  const toPath = pathOnly(to);
  if (toPath === pathname) return true;
  if (toPath === "/") return pathname === "/";
  return pathname.startsWith(toPath + "/");
}

type NavLinkClassName = string | ((state: { isActive: boolean }) => string);
type NavLinkChildren = ReactNode | ((state: { isActive: boolean }) => ReactNode);

interface NavLinkProps
  extends Omit<ComponentProps<typeof Link>, "href" | "className" | "children"> {
  /** Destination path (react-router-compatible prop name; maps to Inertia `href`). */
  to: string;
  /** When true, only an exact path match is active (react-router semantics). */
  end?: boolean;
  className?: NavLinkClassName;
  children?: NavLinkChildren;
}

/**
 * Drop-in replacement for react-router's <NavLink>. Supports the function form
 * of `className` and `children` that receive `{ isActive }`.
 */
export const NavLink = forwardRef<HTMLAnchorElement, NavLinkProps>(
  function NavLink({ to, end, className, children, ...rest }, ref) {
    const pathname = useCurrentPath();
    const isActive = end ? pathname === pathOnly(to) : isPathActive(pathname, to);
    const cls = typeof className === "function" ? className({ isActive }) : className;
    const kids =
      typeof children === "function" ? children({ isActive }) : children;
    return (
      <Link ref={ref} href={to} className={cls} {...rest}>
        {kids}
      </Link>
    );
  },
);
